import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../utils/firebase';
import { format } from 'date-fns';
import Header from '../components/Header';

const AdminSupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [response, setResponse] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    resolved: 0,
    urgent: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'supportTickets'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const ticketList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setTickets(ticketList);
      
      // Calculate stats
      const stats = {
        total: ticketList.length,
        open: ticketList.filter(t => t.status === 'open').length,
        inProgress: ticketList.filter(t => t.status === 'in_progress').length,
        resolved: ticketList.filter(t => t.status === 'resolved').length,
        urgent: ticketList.filter(t => t.priority === 'urgent' && t.status !== 'resolved').length
      };
      setStats(stats);
      
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await updateDoc(doc(db, 'supportTickets', ticketId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      await fetchTickets();
    } catch (error) {
      console.error('Error updating ticket status:', error);
    }
  };

  const handlePriorityChange = async (ticketId, newPriority) => {
    try {
      await updateDoc(doc(db, 'supportTickets', ticketId), {
        priority: newPriority,
        updatedAt: serverTimestamp()
      });
      await fetchTickets();
    } catch (error) {
      console.error('Error updating ticket priority:', error);
    }
  };

  const handleAddResponse = async (ticketId) => {
    if (!response.trim()) return;
    
    try {
      const ticketRef = doc(db, 'supportTickets', ticketId);
      const ticket = tickets.find(t => t.id === ticketId);
      const responses = ticket.responses || [];
      
      responses.push({
        message: response,
        timestamp: serverTimestamp(),
        admin: true
      });
      
      await updateDoc(ticketRef, {
        responses,
        status: 'in_progress',
        updatedAt: serverTimestamp()
      });
      
      setResponse('');
      await fetchTickets();
    } catch (error) {
      console.error('Error adding response:', error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-red-100 text-red-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      account: '👤',
      team: '🏆',
      game: '🧠',
      venue: '📍',
      payment: '💰',
      technical: '🔧',
      feature: '💡',
      other: '📝'
    };
    return icons[category] || '📝';
  };

  const filteredTickets = tickets.filter(ticket => {
    if (filter === 'all') return true;
    if (filter === 'urgent') return ticket.priority === 'urgent' && ticket.status !== 'resolved';
    return ticket.status === filter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading tickets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-green-300 hover:text-green-400 mb-4"
          >
            ← Back to Admin Dashboard
          </button>
          <h1 className="text-3xl font-bold text-white mb-2">Support Tickets</h1>
          <p className="text-gray-300">AI-powered support ticket management system</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-300">Total Tickets</p>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
              </div>
              <div className="text-3xl">📋</div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-300">Open</p>
                <p className="text-2xl font-bold text-red-600">{stats.open}</p>
              </div>
              <div className="text-3xl">🔴</div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-300">In Progress</p>
                <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
              </div>
              <div className="text-3xl">🔄</div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-300">Resolved</p>
                <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
              </div>
              <div className="text-3xl">✅</div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-300">Urgent</p>
                <p className="text-2xl font-bold text-red-600">{stats.urgent}</p>
              </div>
              <div className="text-3xl">🚨</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                filter === 'all' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-700'
              }`}
            >
              All ({stats.total})
            </button>
            <button
              onClick={() => setFilter('urgent')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                filter === 'urgent' ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Urgent ({stats.urgent})
            </button>
            <button
              onClick={() => setFilter('open')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                filter === 'open' ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Open ({stats.open})
            </button>
            <button
              onClick={() => setFilter('in_progress')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                filter === 'in_progress' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-700'
              }`}
            >
              In Progress ({stats.inProgress})
            </button>
            <button
              onClick={() => setFilter('resolved')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                filter === 'resolved' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Resolved ({stats.resolved})
            </button>
          </div>
        </div>

        {/* Tickets List */}
        <div className="bg-gray-800 rounded-lg shadow-md overflow-hidden">
          <div className="p-6 border-b">
            <h2 className="text-xl font-semibold text-white">Tickets ({filteredTickets.length})</h2>
          </div>
          
          {filteredTickets.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No tickets found for the selected filter.
            </div>
          ) : (
            <div className="divide-y divide-gray-700">
              {filteredTickets.map(ticket => (
                <div key={ticket.id} className="p-6 hover:bg-gray-700">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl">{getCategoryIcon(ticket.category)}</span>
                        <h3 className="text-lg font-semibold text-white">{ticket.title}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                          {ticket.status.replace('_', ' ')}
                        </span>
                      </div>
                      
                      <p className="text-gray-300 mb-3">{ticket.description}</p>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                        <span>From: {ticket.userName || ticket.contactEmail}</span>
                        <span>Category: {ticket.category}</span>
                        <span>Created: {ticket.createdAt?.toDate?.() ? format(ticket.createdAt.toDate(), 'MMM d, h:mm a') : 'Recently'}</span>
                      </div>

                      {/* AI Analysis */}
                      {ticket.aiAnalysis && (
                        <div className="bg-blue-800 border border-blue-700 rounded-lg p-3 mb-3">
                          <h4 className="text-sm font-semibold text-blue-300 mb-2">🤖 AI Analysis</h4>
                          <div className="text-sm text-blue-200">
                            {ticket.aiAnalysis.autoCategorized && <div>• Auto-categorized as {ticket.category}</div>}
                            {ticket.aiAnalysis.autoPrioritized && <div>• Auto-prioritized as {ticket.priority}</div>}
                            {ticket.aiAnalysis.suggestedSolutions && (
                              <div className="mt-2">
                                <div className="font-medium">Suggested Solutions:</div>
                                <ul className="list-disc list-inside ml-2">
                                  {ticket.aiAnalysis.suggestedSolutions.map((solution, index) => (
                                    <li key={index}>{solution}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Responses */}
                      {ticket.responses && ticket.responses.length > 0 && (
                        <div className="bg-gray-700 rounded-lg p-3 mb-3">
                          <h4 className="text-sm font-semibold text-white mb-2">Responses</h4>
                          {ticket.responses.map((resp, index) => (
                            <div key={index} className="text-sm text-gray-300 mb-2">
                              <div className="font-medium">{resp.admin ? 'Admin' : 'User'}:</div>
                              <div>{resp.message}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Quick Actions */}
                      <div className="flex items-center gap-2">
                        <select
                          value={ticket.status}
                          onChange={(e) => handleStatusChange(ticket.id, e.target.value)}
                          className="px-3 py-1 border border-gray-700 rounded text-sm"
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                        </select>
                        
                        <select
                          value={ticket.priority}
                          onChange={(e) => handlePriorityChange(ticket.id, e.target.value)}
                          className="px-3 py-1 border border-gray-700 rounded text-sm"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                        
                        <button
                          onClick={() => setSelectedTicket(selectedTicket === ticket.id ? null : ticket.id)}
                          className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                        >
                          {selectedTicket === ticket.id ? 'Cancel' : 'Respond'}
                        </button>
                      </div>

                      {/* Response Form */}
                      {selectedTicket === ticket.id && (
                        <div className="mt-3">
                          <textarea
                            value={response}
                            onChange={(e) => setResponse(e.target.value)}
                            placeholder="Type your response..."
                            className="w-full p-3 border border-gray-700 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            rows={3}
                          />
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={() => handleAddResponse(ticket.id)}
                              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                            >
                              Send Response
                            </button>
                            <button
                              onClick={() => setSelectedTicket(null)}
                              className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-800"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSupportTickets; 